import { useMemo, useState } from 'react';
import {
  ArrowRight,
  AlertCircle,
  FileCheck2,
  RefreshCw,
  Send,
  Loader2,
  XCircle,
} from 'lucide-react';
import { Badge } from '@client/components/ui/badge';
import { useClientPolicyApplication } from '@client/hooks/useClientPolicyApplication';
import { useDocuSignEnvelopeStatus } from '@client/hooks/useDocuSignEnvelopeStatus';
import { docusignRoles, FundingPolicyType, SessionType } from '@shared';
import { StepAlert, StepActions, StepActionButton } from '../ApplicationStepComponents';
import { Button } from '../ui/button';
import { FileTextIcon } from './FileTextIcon';
import { useDispatch, useSelector } from 'react-redux';
import { postDocusignEnvelopeForClientByFormIdRequest } from '@client/model/policyApplicationModel';
import { selectCachedFormConfig, AppState } from '@client/model';
import { FormDataComparator } from './FormDataComparator';

export function SignApplicationFormStep({
  isTestDocusignDataModalOpen,
  onOpenChange,
}: {
  isTestDocusignDataModalOpen: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { policyApplication, clientPolicyApplicationId, policyFundingSource } =
    useClientPolicyApplication();
  const dispatch = useDispatch();
  const [envelopeSentSuccessfully, setEnvelopeSentSuccessfully] = useState(false);

  // Use the custom DocuSign envelope status hook
  const {
    docusignEnvelopeStatus,
    isPostingEnvelope,
    isFetchingEnvelopeStatus,
    hasSubmittedEnvelope,
    docusignSubmissionDataComparison,
  } = useDocuSignEnvelopeStatus({
    clientPolicyApplicationId,
    onEnvelopeSentSuccess: () => setEnvelopeSentSuccessfully(true),
  });

  // Get the sign-application-form step
  const signApplicationStep = useMemo(
    () => policyApplication?.steps?.find(step => step.stepType === 'sign-application-form'),
    [policyApplication?.steps],
  );
  const applicationFormStep = useMemo(
    () => policyApplication?.steps?.find(step => step.stepType === 'application-form'),
    [policyApplication?.steps],
  );
  const illustrationStep = useMemo(
    () => policyApplication?.steps?.find(step => step.stepType === 'download-policy-illustration'),
    [policyApplication?.steps],
  );

  // Lincoln MoneyGuard signs the policy illustration alongside the application, so the illustration must be downloaded before sending for signature.
  const requiresIllustrationBeforeSigning =
    policyFundingSource?.policyType === FundingPolicyType.moneyGuardFixedAdvantage;
  const illustrationNotReady =
    requiresIllustrationBeforeSigning && illustrationStep?.status !== 'completed';

  // Get form config from Redux cache (for template ID)
  const policyApplicationFormId = applicationFormStep?.policyApplicationFormId;
  const formConfig = useSelector((state: AppState) =>
    policyApplicationFormId ? selectCachedFormConfig(state, policyApplicationFormId) : null,
  );

  // Extract signers from step metadata
  const signers: Array<{
    roleKey: DocusignRoleKey;
    name: string;
    email: string;
    status: DocusignRecipientStatus;
    hasSigned: boolean;
  }> = useMemo(() => {
    const recipients = signApplicationStep?.metadata?.recipients;
    if (!recipients || !Array.isArray(recipients)) {
      return [];
    }
    return recipients;
  }, [signApplicationStep?.metadata?.recipients]);

  // Group signers by email and name (case insensitive)
  const groupedSigners = useMemo(() => {
    const groups = new Map<
      string,
      {
        roleKeys: DocusignRoleKey[];
        name: string;
        email: string;
        status: DocusignRecipientStatus;
        hasSigned: boolean;
      }
    >();

    signers.forEach(signer => {
      const key = `${signer.email.toLowerCase()}_${signer.name.toLowerCase()}`;

      if (groups.has(key)) {
        const existing = groups.get(key)!;
        existing.roleKeys.push(signer.roleKey);
        // Use the first non-null/undefined/unknown status
        if (existing.status === null || existing.status === undefined) {
          existing.status = signer.status;
        }
      } else {
        groups.set(key, {
          roleKeys: [signer.roleKey],
          name: signer.name,
          email: signer.email,
          status: signer.status,
          hasSigned: signer.hasSigned,
        });
      }
    });

    return Array.from(groups.values());
  }, [signers]);

  const getStatusBadgeConfig = (status: DocusignRecipientStatus) => {
    switch (status) {
      case 'recipient_completed':
        return {
          label: 'Signed',
          className: 'bg-green-100 text-green-700 hover:bg-green-200',
          icon: <FileCheck2 className="h-3 w-3" />,
        };
      case 'recipient_delivered':
        return {
          label: 'Delivered',
          className: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
          icon: <FileTextIcon className="h-3 w-3" />,
        };
      case 'recipient_sent':
        return {
          label: 'Sent',
          className: 'bg-amber-100 text-amber-700 hover:bg-amber-200',
          icon: <ArrowRight className="h-3 w-3" />,
        };
      case 'recipient_declined':
        return {
          label: 'Declined',
          className: 'bg-red-100 text-red-700 hover:bg-red-200',
          icon: <AlertCircle className="h-3 w-3" />,
        };
      case 'invalid_signer_configuration':
        return {
          label: 'Configuration Error',
          className: 'bg-red-100 text-red-700 hover:bg-red-200',
          icon: <XCircle className="h-3 w-3" />,
        };
      default:
        return {
          label: 'Not sent',
          className: 'bg-gray-100 text-gray-700 hover:bg-gray-200',
          icon: null,
        };
    }
  };
  const completedCount = groupedSigners.filter(s => s.status === 'recipient_completed').length;
  const totalCount = groupedSigners.length;

  // Check if any signers have invalid configuration (no tabs in DocuSign template)
  const hasInvalidSignerConfiguration = useMemo(
    () => groupedSigners.some(s => s.status === 'invalid_signer_configuration'),
    [groupedSigners],
  );

  // Check if all recipients have undefined status
  const allRecipientsUndefined = useMemo(
    () => groupedSigners.every(s => !s.status || s.status === undefined),
    [groupedSigners],
  );

  const showSendForSignatureButton = useMemo(
    () =>
      // Show button if recipients are not yet sent OR if there's an invalid signer configuration (allow retry)
      (allRecipientsUndefined || hasInvalidSignerConfiguration) &&
      !envelopeSentSuccessfully &&
      !isFetchingEnvelopeStatus &&
      !(docusignEnvelopeStatus == 'sent' || docusignEnvelopeStatus == 'created'),
    [
      allRecipientsUndefined,
      hasInvalidSignerConfiguration,
      envelopeSentSuccessfully,
      docusignEnvelopeStatus,
      isFetchingEnvelopeStatus,
    ],
  );
  const showSuccessAlert = useMemo(
    () => allRecipientsUndefined && envelopeSentSuccessfully,
    [allRecipientsUndefined, envelopeSentSuccessfully],
  );

  // Check if at least one recipient has been sent or delivered
  const hasRecipientActivity = useMemo(
    () =>
      groupedSigners.some(s => s.status === 'recipient_delivered' || s.status === 'recipient_sent'),
    [groupedSigners],
  );
  const showPendingSignaturesAlert = useMemo(
    () => hasRecipientActivity && completedCount < totalCount,
    [hasRecipientActivity, completedCount, totalCount],
  );
  const showAlreadySentAlert = useMemo(
    () => !showPendingSignaturesAlert && !showSuccessAlert && hasSubmittedEnvelope,
    [showPendingSignaturesAlert, showSuccessAlert, hasSubmittedEnvelope],
  );

  const handleSendForSignature = () => {
    const clientId = policyApplication?.clientId;
    if (!clientPolicyApplicationId || !policyApplicationFormId || !clientId) return;

    const templateId = formConfig?.formMetadata?.templateId;
    if (!templateId) {
      console.error('Template ID not found for form:', policyApplicationFormId);
    }

    dispatch(
      postDocusignEnvelopeForClientByFormIdRequest({
        clientPolicyApplicationId,
        formId: policyApplicationFormId,
        templateId,
        mode: 'envelope',
        recipients: signers,
        forClientId: clientId,
        policyFundingSource,
      }),
    );
  };

  // Handle case where there are no signers yet
  if (totalCount === 0) {
    return (
      <div className="mx-auto mt-8 max-w-2xl">
        <StepAlert
          title="No Signers Configured"
          description="The application form has not been configured with signers yet. Please complete the previous steps to set up the required signers."
          variant="warning"
        />
      </div>
    );
  }

  return (
    <>
      <div className="mx-auto mt-8 max-w-2xl space-y-4">
        {/* Refresh Button */}
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => window.location.reload()} // eventually make this refresh more local to just refreshing the application state
            className="flex items-center gap-1.5 rounded-md bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-700"
          >
            <RefreshCw className="h-3 w-3" />
            Refresh Status
          </Button>
        </div>

        {/* Progress Summary */}
        <div
          className="bg-primary-50 mb-6 rounded-lg p-4"
          data-testid="sign-application-form-step-title"
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Signing Progress</span>
            <span className="text-primary-700 text-sm font-semibold">
              {completedCount} of {totalCount} completed
            </span>
          </div>
          <div className="mt-2 w-full rounded-full bg-gray-200">
            <div
              className="bg-primary-500 h-2 rounded-full transition-all duration-300"
              style={{
                width: `${(completedCount / totalCount) * 100}%`,
              }}
            />
          </div>
        </div>

        {/* Signers List */}
        <div
          className="space-y-3"
          data-testid="sign-application-form-signers-list"
        >
          {groupedSigners.map((signer, index) => {
            const roleNames = signer.roleKeys
              .map(roleKey => docusignRoles[roleKey].roleName)
              .join(', ');
            const statusConfig = getStatusBadgeConfig(signer.status);

            return (
              <div
                key={index}
                className="flex flex-col rounded-lg border border-gray-200 bg-white p-4 transition-all hover:shadow-sm sm:flex-row sm:items-center sm:justify-between"
                data-testid="sign-application-form-signer-card"
              >
                <div className="flex-1 text-left">
                  <div className="flex items-start justify-between sm:items-center">
                    <div>
                      <p
                        className="text-base font-semibold text-gray-900"
                        data-testid="signer-name"
                      >
                        {signer.name}
                      </p>
                      <p
                        className="text-sm text-gray-700"
                        data-testid="signer-email"
                      >
                        {signer.email}
                      </p>
                      <p
                        className="text-xs text-gray-700"
                        data-testid="signer-roles"
                      >
                        {roleNames}
                      </p>
                    </div>
                    <Badge
                      className={`mt-2 flex items-center gap-1 sm:mt-0 ${statusConfig.className}`}
                      data-testid="signer-status-badge"
                    >
                      {statusConfig.icon}
                      {statusConfig.label}
                    </Badge>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {hasInvalidSignerConfiguration && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4 text-red-800">
            <div className="flex items-start gap-2">
              <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium">DocuSign Template Configuration Error</h3>
                <p className="mt-1 text-sm">
                  Some signers are missing required signature fields in the template, each signer
                  must have a signature field assigned before you can continue.
                </p>
                <p className="mt-2 text-xs text-red-700">
                  Please contact support to resolve this issue with the template configuration.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Additional Information */}
        {showSendForSignatureButton && !showAlreadySentAlert && (
          <>
            {illustrationNotReady && (
              <StepAlert
                title="Policy illustration required"
                description="The policy illustration must be downloaded before sending the application for signature."
                variant="warning"
              />
            )}
            <StepActions>
              <StepActionButton
                onClick={handleSendForSignature}
                disabled={isPostingEnvelope || illustrationNotReady}
                testId="sign-application-form-send-button"
                icon={
                  isPostingEnvelope ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="ml-1 h-5 w-5" />
                  )
                }
              >
                {isPostingEnvelope
                  ? 'Sending...'
                  : hasInvalidSignerConfiguration
                    ? 'Retry Send for Signature'
                    : 'Send for Signature'}
              </StepActionButton>
            </StepActions>
          </>
        )}

        {/* Show status message after successful envelope submission */}
        {showSuccessAlert && (
          <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-4 text-green-800">
            <div className="flex items-center gap-2">
              <FileCheck2 className="h-5 w-5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium">Application Forms Sent Successfully</h3>
                <p className="mt-1 text-sm">Status updates in 1–2 minutes.</p>
                <p className="mt-1 text-xs text-green-700">
                  Use the "Refresh Status" button above to check for updates.
                </p>
              </div>
            </div>
          </div>
        )}

        {showPendingSignaturesAlert && (
          <div className="mt-4 rounded-md border border-blue-200 bg-blue-50 p-4 text-blue-800">
            <div className="flex w-full items-center justify-between">
              <div className="flex-1">
                <h3 className="text-sm font-medium">Signatures Pending</h3>
                <p className="mt-1 text-sm">{`${
                  totalCount - completedCount
                } recipient(s) still need to sign the application form. `}</p>
                <p className="mt-1 text-sm">
                  You cannot proceed until all signatures are collected.
                </p>
              </div>
            </div>
          </div>
        )}

        {showAlreadySentAlert && (
          <StepAlert
            title="Application Forms Sent"
            description="The application has already been sent for signature. Status updates in 1–2 minutes."
            variant="info"
          />
        )}

        {completedCount === totalCount && (
          <StepAlert
            title="All Signatures Collected"
            description="Everyone has signed the application form. The application will proceed to the next step automatically."
            variant="info"
          />
        )}

        {!showSendForSignatureButton && (
          <FormDataComparator
            docuSignData={docusignSubmissionDataComparison?.processedDocusignData}
            submissionData={docusignSubmissionDataComparison?.submissionData}
            open={isTestDocusignDataModalOpen}
            onOpenChange={onOpenChange}
          />
        )}
      </div>
    </>
  );
}
